/**
 * Modal for creating a new chat group.
 */

import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Stack,
  Alert,
  Checkbox,
  List,
  ListItem,
  Avatar,
  Box,
  CircularProgress,
  Chip,
} from '@mui/joy';
import { MagnifyingGlass as SearchIcon, User as PersonIcon } from '@phosphor-icons/react';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import { useCreateChatGroup } from '../../hooks/useChat';

interface CreateGroupModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (channelId: string) => void;
  currentUserId?: string | number;
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  open,
  onClose,
  onSuccess,
  currentUserId,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { users, loading: usersLoading } = useOrganizationUsers();
  const createGroup = useCreateChatGroup();

  // Filter users by search query and exclude current user
  const filteredUsers = users.filter((user) => {
    if (currentUserId && String(user.id) === String(currentUserId)) {
      return false;
    }
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query)
    );
  });

  const toggleUser = (userId: number) => {
    const idStr = String(userId);
    setSelectedUserIds((prev) =>
      prev.includes(idStr) ? prev.filter((id) => id !== idStr) : [...prev, idStr]
    );
  };

  const getSelectedUsers = () => {
    return users.filter((user) => selectedUserIds.includes(String(user.id)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      const result = await createGroup.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        member_ids: selectedUserIds.length > 0 ? selectedUserIds : undefined,
      });

      // Reset form
      setName('');
      setDescription('');
      setSelectedUserIds([]);
      setSearchQuery('');

      onClose();
      // Pass the channel ID so the chat UI can select it
      if (result?.channel?.id) {
        onSuccess?.(result.channel.id);
      }
    } catch (err: unknown) {
      // Extract error message from API response if available
      let errorMessage = 'Failed to create group';
      if (err && typeof err === 'object') {
        const axiosError = err as { response?: { data?: { detail?: string; error?: string; message?: string } }; message?: string };
        if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail;
        } else if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      }
      setError(errorMessage);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setSelectedUserIds([]);
    setSearchQuery('');
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ minWidth: 500, maxHeight: '85vh', overflow: 'hidden' }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          Create Group
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            <FormControl required>
              <FormLabel>Group Name</FormLabel>
              <Input
                placeholder="e.g., Project Team, Sales"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="What's this group for?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                minRows={2}
              />
            </FormControl>

            <Box>
              <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                Add Members (Optional)
              </Typography>
              <Input
                placeholder="Search users..."
                startDecorator={<SearchIcon />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 1 }}
              />

              {selectedUserIds.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {getSelectedUsers().map((user) => (
                    <Chip
                      key={user.id}
                      size="sm"
                      variant="soft"
                      color="primary"
                      endDecorator={
                        <Box
                          component="span"
                          sx={{ cursor: 'pointer', ml: 0.5 }}
                          onClick={() => toggleUser(user.id)}
                        >
                          ×
                        </Box>
                      }
                    >
                      {user.full_name || user.email}
                    </Chip>
                  ))}
                </Box>
              )}

              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                {usersLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                ) : filteredUsers.length === 0 ? (
                  <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}>
                    {searchQuery ? 'No users found' : 'No users available'}
                  </Typography>
                ) : (
                  <List size="sm">
                    {filteredUsers.map((user) => (
                      <ListItem key={user.id}>
                        <Checkbox
                          overlay
                          checked={selectedUserIds.includes(String(user.id))}
                          onChange={() => toggleUser(user.id)}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar size="sm">
                                {user.first_name?.[0] || user.email?.[0] || <PersonIcon />}
                              </Avatar>
                              <Box>
                                <Typography level="body-sm">
                                  {user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
                                </Typography>
                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                  {user.email}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="plain" color="neutral" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createGroup.isPending}
                disabled={!name.trim()}
              >
                Create Group
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default CreateGroupModal;
